import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FileText, Save } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CALGARY_AREA_LOCATIONS = [
  'Calgary',
  'Airdrie',
  'Chestermere',
  'Cochrane',
  'Okotoks',
  'High River',
  'Strathmore',
  'Canmore',
  'Banff',
  'Black Diamond',
  'Turner Valley',
  'Crossfield',
  'Carstairs',
  'Didsbury',
  'Nanton',
  'Irricana',
  'Beiseker',
  'Bragg Creek'
];

const STANDARD_SERVICE_TYPES = ['Plumbing', 'HVAC', 'Electrical', 'Landscaping', 'Junk Removal', 'Cleaning', 'Handyman'];

const QuoteForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editQuote = location.state?.editQuote;
  
  const [settings, setSettings] = useState(null);
  const [quoteNumberOverride, setQuoteNumberOverride] = useState(null);
  
  // Derive isEditMode from editQuote presence
  const isEditMode = Boolean(editQuote);
  
  // Compute quote number: editQuote > override > computed from settings
  const quoteNumber = editQuote?.quote_number || 
    quoteNumberOverride || 
    (settings ? `${settings.quote_prefix}${settings.current_quote_number + settings.quote_increment}` : '');
  
  const setQuoteNumber = (value) => setQuoteNumberOverride(value);
  
  // Initialize form data - either from editQuote or empty
  const getInitialFormData = () => {
    if (editQuote) {
      const isStandardService = STANDARD_SERVICE_TYPES.includes(editQuote.service_type);
      return {
        customerName: editQuote.customer_name || '',
        customerEmail: editQuote.customer_email || '',
        customerAddress: editQuote.customer_address || '',
        phoneNumber: editQuote.phone_number || '',
        serviceType: isStandardService ? editQuote.service_type : 'Other',
        customServiceType: isStandardService ? '' : editQuote.service_type,
        location: editQuote.location || '',
        description: editQuote.description || '',
        price: editQuote.price || '',
        notes: editQuote.notes || ''
      };
    }
    return {
      customerName: '',
      customerEmail: '',
      customerAddress: '',
      phoneNumber: '',
      serviceType: '',
      customServiceType: '',
      location: '',
      description: '',
      price: '',
      notes: ''
    };
  };
  
  const [formData, setFormData] = useState(getInitialFormData);

  // Load settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const token = localStorage.getItem('session_token');
        const response = await axios.get(`${API}/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSettings(response.data);
      } catch (error) {
        console.error('Error loading settings:', error);
      }
    };
    
    loadSettings();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleQuoteNumberChange = (e) => {
    setQuoteNumber(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    const effectiveServiceType = formData.serviceType === 'Other' ? formData.customServiceType : formData.serviceType;
    
    if (!formData.customerName || !formData.phoneNumber || !effectiveServiceType || !formData.price || !formData.location) {
      alert('Please fill in all required fields (Customer Name, Phone, Service Type, Location, and Price)');
      return;
    }

    try {
      const token = localStorage.getItem('session_token');
      
      // Build quote data
      const quoteData = {
        quote_number: quoteNumber,
        date: isEditMode ? editQuote.date : new Date().toLocaleDateString('en-CA'),
        customer_name: formData.customerName,
        customer_email: formData.customerEmail || null,
        customer_address: formData.customerAddress,
        phone_number: formData.phoneNumber,
        service_type: effectiveServiceType,
        location: formData.location,
        description: formData.description,
        price: formData.price,
        notes: formData.notes
      };

      let quoteId;

      if (isEditMode) {
        // Update existing quote
        await axios.put(`${API}/quotes/${editQuote.id}`, quoteData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        quoteId = editQuote.id;
      } else {
        // Create new quote
        const response = await axios.post(`${API}/quotes`, quoteData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        quoteId = response.data.id;

        // Update current_quote_number in settings (only for new quotes)
        const numberPart = parseInt(quoteNumber.replace(settings.quote_prefix, ''));
        await axios.put(`${API}/settings`, 
          { current_quote_number: numberPart },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      // Navigate to preview
      navigate(`/preview/${quoteId}`);
    } catch (error) {
      console.error('Error saving quote:', error);
      alert(`Failed to ${isEditMode ? 'update' : 'create'} quote. Please try again.`);
    }
  };

  const handleCancelEdit = () => {
    // Clear location state and navigate to create new quote mode
    navigate('/', { replace: true, state: {} });
  };

  if (!settings) {
    return <div>Loading...</div>;
  }

  return (
    <div className="form-container">
      <div className="form-header">
        <h2>{isEditMode ? 'Edit Quote' : 'Create New Quote'}</h2>
        <p>{isEditMode ? 'Update the quote details below' : 'Fill in the details below to generate a professional quote'}</p>
        {isEditMode && (
          <button 
            type="button" 
            onClick={handleCancelEdit}
            style={{
              marginTop: '0.5rem',
              padding: '0.5rem 1rem',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            Cancel Edit
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label className="form-label">Quote Number {!isEditMode && '(editable)'}</label>
          <input
            type="text"
            className="form-input"
            value={quoteNumber}
            onChange={handleQuoteNumberChange}
            disabled={isEditMode}
            style={isEditMode ? { backgroundColor: '#e9ecef', cursor: 'not-allowed' } : {}}
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Customer Name *</label>
            <input
              type="text"
              name="customerName"
              className="form-input"
              value={formData.customerName}
              onChange={handleChange}
              placeholder="Name..."
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Phone Number *</label>
            <input
              type="tel"
              name="phoneNumber"
              className="form-input"
              value={formData.phoneNumber}
              onChange={handleChange}
              placeholder="(403) 555-1234"
              required
            />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Customer Email</label>
          <input
            type="email"
            name="customerEmail"
            className="form-input"
            value={formData.customerEmail}
            onChange={handleChange}
            placeholder="customer@email.com"
          />
        </div>

        <div className="form-group">
          <label className="form-label">Customer Address</label>
          <input
            type="text"
            name="customerAddress"
            className="form-input"
            value={formData.customerAddress}
            onChange={handleChange}
            placeholder="123 Main Street, Calgary, AB"
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Service Type *</label>
            <select
              name="serviceType"
              className="form-select"
              value={formData.serviceType}
              onChange={handleChange}
              required
            >
              <option value="">Select a service...</option>
              <option value="Plumbing">Plumbing</option>
              <option value="HVAC">HVAC</option>
              <option value="Electrical">Electrical</option>
              <option value="Landscaping">Landscaping</option>
              <option value="Junk Removal">Junk Removal</option>
              <option value="Cleaning">Cleaning</option>
              <option value="Handyman">Handyman Services</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Service Location *</label>
            <select
              name="location"
              className="form-select"
              value={formData.location}
              onChange={handleChange}
              required
            >
              <option value="">Select location...</option>
              {CALGARY_AREA_LOCATIONS.map(loc => (
                <option key={loc} value={loc}>{loc}</option>
              ))}
            </select>
          </div>
        </div>

        {formData.serviceType === 'Other' && (
          <div className="form-group">
            <label className="form-label">Custom Service Type *</label>
            <input
              type="text"
              name="customServiceType"
              className="form-input"
              value={formData.customServiceType}
              onChange={handleChange}
              placeholder="Enter service type..."
              required
            />
          </div>
        )}

        <div className="form-group">
          <label className="form-label">Description of Work *</label>
          <textarea
            name="description"
            className="form-textarea"
            value={formData.description}
            onChange={handleChange}
            placeholder="Describe the work to be done..."
            required
          />
        </div>

        <div className="form-group">
          <label className="form-label">Total Price (CAD) *</label>
          <input
            type="number"
            name="price"
            className="form-input"
            value={formData.price}
            onChange={handleChange}
            placeholder="500.00"
            step="0.01"
            min="0"
            required
          />
        </div>

        <div className="form-group">
          <label className="form-label">Additional Notes</label>
          <textarea
            name="notes"
            className="form-textarea"
            value={formData.notes}
            onChange={handleChange}
            placeholder="Any additional information..."
            style={{ minHeight: '100px' }}
          />
        </div>

        <button type="submit" className="btn btn-primary btn-large">
          {isEditMode ? <Save size={24} /> : <FileText size={24} />}
          {isEditMode ? 'Update Quote' : 'Generate Quote'}
        </button>
      </form>
    </div>
  );
};

export default QuoteForm;
